// pages/Agents/AgentSpace/AgentSpaceDashboard.js
// Replaces the old "task summaries" dashboard. This is now a flat list of all
// agents (NOT a table) â€” each row shows the agent resource, its configured
// status, and a gear icon. Clicking the row opens the agent detail page.
import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Skeleton,
    Chip,
    Tooltip,
} from '@mui/material';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import PollIcon from '@mui/icons-material/Poll';
import PeopleIcon from '@mui/icons-material/People';
import ChatIcon from '@mui/icons-material/Chat';
import EventIcon from '@mui/icons-material/Event';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import EmailIcon from '@mui/icons-material/Email';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import axiosInstance from '../../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const colors = {
    textPrimary: '#1e293b',
    textSecondary: '#475569',
    textTertiary: '#94a3b8',
    accent: '#8b5cf6',
    success: '#059669',
};

const ICON_MAP = {
    PollIcon: (size) => <PollIcon sx={{ fontSize: size }} />,
    PeopleIcon: (size) => <PeopleIcon sx={{ fontSize: size }} />,
    ChatIcon: (size) => <ChatIcon sx={{ fontSize: size }} />,
    EventIcon: (size) => <EventIcon sx={{ fontSize: size }} />,
    MailOutlineIcon: (size) => <MailOutlineIcon sx={{ fontSize: size }} />,
    EmailIcon: (size) => <EmailIcon sx={{ fontSize: size }} />,
    CalendarMonthIcon: (size) => <CalendarMonthIcon sx={{ fontSize: size }} />,
    CleaningServicesIcon: (size) => <CleaningServicesIcon sx={{ fontSize: size }} />,
};

function getIcon(iconName, size = 20) {
    const fn = ICON_MAP[iconName];
    return fn ? fn(size) : <AutoAwesomeIcon sx={{ fontSize: size }} />;
}

export default function AgentSpaceDashboard({ chatbotId, onSelectAgent, onConfigureAgent, showHeader = true }) {
    const [agentTypes, setAgentTypes] = useState([]);
    const [connectionStatuses, setConnectionStatuses] = useState({});
    const [loading, setLoading] = useState(true);

    // Fetch agent types + their connection statuses in parallel
    useEffect(() => {
        if (!chatbotId) return;
        let cancelled = false;
        setLoading(true);
        Promise.all([
            axiosInstance.get('/api/agent-space/agent-types'),
            axiosInstance.get(`/api/agent-space/connections?chatbot_id=${chatbotId}`),
        ])
            .then(([typesRes, connRes]) => {
                if (cancelled) return;
                if (typesRes.data?.success) setAgentTypes(typesRes.data.data || []);
                if (connRes.data?.success) setConnectionStatuses(connRes.data.data || {});
            })
            .catch(() => { /* render falls back to whatever we have */ })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [chatbotId]);

    const rows = useMemo(() => agentTypes.map((a) => {
        // gmail_cleanup reuses gmail connection
        const connKey = a.connection_type || a.id;
        const conn = connectionStatuses[connKey];
        const isConfigured = a.requires_connection ? !!conn?.is_connected : true;
        return { ...a, isConfigured, connectedEmail: conn?.connected_email || null };
    }), [agentTypes, connectionStatuses]);

    return (
        <Box sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'rgba(255,255,255,0.6)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(0,0,0,0.06)',
            borderRadius: '16px',
            overflow: 'hidden',
        }}>
            {showHeader && (
                <Box sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 2.5 }, pb: 1.5, borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
                    <Typography sx={{ fontFamily, fontSize: '0.95rem', fontWeight: 700, color: colors.textPrimary }}>
                        Agents
                    </Typography>
                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: colors.textTertiary, mt: 0.25 }}>
                        Select an agent to view its activity, schedule tasks, or start a conversation.
                    </Typography>
                </Box>
            )}

            <Box sx={{
                flex: 1, overflow: 'auto',
                px: { xs: 1, sm: 1.5 }, py: 1,
                '&::-webkit-scrollbar': { width: '6px' },
                '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.1)', borderRadius: '4px' },
            }}>
                {loading ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1 }}>
                        {[1, 2, 3, 4, 5].map((i) => (
                            <Skeleton key={i} variant="rounded" height={64} sx={{ borderRadius: '12px' }} />
                        ))}
                    </Box>
                ) : rows.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <AutoAwesomeIcon sx={{ fontSize: 36, color: 'rgba(0,0,0,0.12)', mb: 1 }} />
                        <Typography sx={{ fontFamily, fontSize: '0.85rem', color: colors.textTertiary }}>
                            No agents available
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, py: 0.5 }}>
                        {rows.map((agent) => {
                            const agentColor = agent.color || colors.accent;
                            return (
                                <Box
                                    key={agent.id}
                                    onClick={() => onSelectAgent && onSelectAgent(agent.id)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: { xs: 1, sm: 1.5 },
                                        px: { xs: 1.25, sm: 1.75 },
                                        py: { xs: 1, sm: 1.25 },
                                        borderRadius: '12px',
                                        cursor: 'pointer',
                                        border: '1px solid rgba(0,0,0,0.06)',
                                        bgcolor: 'rgba(255,255,255,0.7)',
                                        transition: 'all 0.15s ease',
                                        '&:hover': {
                                            transform: 'translateY(-1px)',
                                            borderColor: `${agentColor}40`,
                                            bgcolor: 'rgba(255,255,255,0.95)',
                                            boxShadow: `0 2px 12px ${agentColor}14`,
                                            '& .row-chevron': { color: agentColor, transform: 'translateX(2px)' },
                                        },
                                    }}
                                >
                                    {/* Small icon */}
                                    <Box sx={{
                                        width: 36, height: 36, borderRadius: '10px',
                                        bgcolor: `${agentColor}14`, color: agentColor,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        flexShrink: 0,
                                    }}>
                                        {getIcon(agent.icon_name, 20)}
                                    </Box>

                                    {/* Label + description */}
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography sx={{
                                            fontFamily, fontSize: '0.85rem', fontWeight: 600,
                                            color: colors.textPrimary, lineHeight: 1.3,
                                        }} noWrap>
                                            {agent.label}
                                        </Typography>
                                        <Typography sx={{
                                            fontFamily, fontSize: '0.7rem', color: colors.textTertiary,
                                            mt: 0.25, lineHeight: 1.3,
                                        }} noWrap>
                                            {agent.description}
                                        </Typography>
                                    </Box>

                                    {/* Status pill */}
                                    <Box sx={{ flexShrink: 0, display: { xs: 'none', sm: 'block' } }}>
                                        {agent.isConfigured ? (
                                            <Chip
                                                size="small"
                                                icon={<CheckCircleOutlineIcon sx={{ fontSize: '14px !important', color: `${colors.success} !important` }} />}
                                                label={agent.connectedEmail ? 'Connected' : 'Configured'}
                                                sx={{
                                                    fontFamily, fontSize: '0.65rem', fontWeight: 600,
                                                    height: 22,
                                                    color: colors.success,
                                                    bgcolor: 'rgba(5,150,105,0.08)',
                                                    border: '1px solid rgba(5,150,105,0.18)',
                                                    '& .MuiChip-icon': { ml: '6px' },
                                                }}
                                            />
                                        ) : (
                                            <Chip
                                                size="small"
                                                icon={<RadioButtonUncheckedIcon sx={{ fontSize: '14px !important', color: '#94a3b8 !important' }} />}
                                                label="Not configured"
                                                sx={{
                                                    fontFamily, fontSize: '0.65rem', fontWeight: 600,
                                                    height: 22,
                                                    color: colors.textTertiary,
                                                    bgcolor: 'rgba(0,0,0,0.04)',
                                                    border: '1px solid rgba(0,0,0,0.08)',
                                                    '& .MuiChip-icon': { ml: '6px' },
                                                }}
                                            />
                                        )}
                                    </Box>

                                    {/* Mobile-only status dot (since pill is hidden xs) */}
                                    <Box sx={{
                                        display: { xs: 'block', sm: 'none' },
                                        width: 8, height: 8, borderRadius: '50%',
                                        bgcolor: agent.isConfigured ? colors.success : 'rgba(0,0,0,0.15)',
                                        flexShrink: 0,
                                    }} />

                                    {/* Gear icon */}
                                    <Tooltip title="Agent settings">
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (onConfigureAgent) onConfigureAgent(agent.id);
                                                else if (onSelectAgent) onSelectAgent(agent.id);
                                            }}
                                            sx={{
                                                color: colors.textTertiary,
                                                '&:hover': { color: agentColor, bgcolor: `${agentColor}10` },
                                            }}
                                        >
                                            <SettingsOutlinedIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </Tooltip>

                                    {/* Chevron */}
                                    <ChevronRightIcon
                                        className="row-chevron"
                                        sx={{
                                            fontSize: 18, color: colors.textTertiary,
                                            transition: 'all 0.15s ease', flexShrink: 0,
                                        }}
                                    />
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </Box>
        </Box>
    );
}
